import React from 'react';
import { styled } from 'styled-components';
import { Panel } from './Panel';
import { ControlButton } from './ControlButton';
import { ReactComponent as PlaysIcon } from '../assets/play-browser-icon.svg';
import { ReactComponent as BoardIcon } from '../assets/waterpolo-halfcourt-icon-v4.svg';

import {
  UserIcon as SettingsIcon,
  BanknotesIcon as AdminIcon,
  MoonIcon as DarkModeIcon,
  SunIcon as LightModeIcon,
  CalendarDaysIcon as CalendarIcon,
  DeviceTabletIcon as TabletIcon,
  // PencilSquareIcon as NotesIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

export enum ViewType {
  PLAY_BROWSER,
  PLAY,
  SETTINGS,
  NOTES,
  ADMIN,
  UPGRADE,
}

const MenuContainer = styled(Panel)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  margin-right: 0.4rem;
`;

export const Menu: React.FC<{
  activeView: ViewType;
  onViewChange: (ViewType) => void;
  isAdmin?: boolean;
  isPro?: boolean;
  colorMode?: 'light' | 'dark';
  onColorModeChange?: (mode: 'light' | 'dark') => void;
  onUpgrade?: () => void;
}> = ({
  activeView,
  onViewChange,
  isAdmin = false,
  isPro = false,
  onColorModeChange,
  colorMode,
  onUpgrade,
}) => {
  const onSelectMenu = (view: ViewType) => () => {
    // If we're on any menu and want to go back to the play view,
    // we should just tap the menu button once again
    if (view === activeView && activeView !== ViewType.PLAY) {
      return onViewChange(ViewType.PLAY);
    }
    return onViewChange(view);
  };

  const navigate = useNavigate();

  // The Planner is a Pro feature: everyone sees the entry point, but without a
  // Pro membership the tap opens the upgrade view instead of the /planner route
  // (which guards itself the same way).
  const hasPlannerAccess = isPro || isAdmin;

  const onSelectPlanner = () => {
    if (!hasPlannerAccess) {
      onUpgrade?.();
      return;
    }
    navigate('/planner');
  };

  return (
    <MenuContainer>
      <ControlButton
        icon={<BoardIcon style={{ width: '26px', height: '26px' }} />}
        onClick={() => onViewChange(ViewType.PLAY)}
        isActive={activeView === ViewType.PLAY}
        large
        inverse
      />
      <ControlButton
        icon={<PlaysIcon />}
        onClick={onSelectMenu(ViewType.PLAY_BROWSER)}
        isActive={activeView === ViewType.PLAY_BROWSER}
        large
        inverse
      />
      {/* <ControlButton
        icon={<NotesIcon />}
        onClick={() => {
          onViewChange(ViewType.NOTES)
        }}
        isActive={activeView === ViewType.NOTES}
        large
        inverse
      /> */}
      <ControlButton
        icon={<SettingsIcon />}
        onClick={onSelectMenu(ViewType.SETTINGS)}
        isActive={activeView === ViewType.SETTINGS}
        large
        inverse
      />
      <ControlButton
        icon={colorMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
        onClick={() => {
          if (colorMode === 'dark') {
            onColorModeChange?.('light');
          } else {
            onColorModeChange?.('dark');
          }
        }}
        large
        inverse
      />
      {isAdmin && (
        <ControlButton
          icon={<AdminIcon />}
          onClick={onSelectMenu(ViewType.ADMIN)}
          isActive={activeView === ViewType.ADMIN}
          large
          inverse
        />
      )}
      <ControlButton
        icon={<CalendarIcon />}
        onClick={onSelectPlanner}
        onlyPro={true}
        isPro={hasPlannerAccess}
        large
        inverse
      />
    </MenuContainer>
  );
};
